/**
 * TESTING ONLY — remove before production.
 *
 * Lets one deployment be pointed at any fair by visiting `/?domain=<host>`, so
 * the sibling switcher can be exercised without standing up a deployment per
 * country. The chosen fair is kept in `localStorage` and sent as `?domain=` on
 * every API call, overriding the `Host` header the API would otherwise resolve.
 *
 * The whole mechanism is gated behind `VITE_ALLOW_DOMAIN_OVERRIDE`. Left unset
 * — as it must be in production — `readFairDomain` returns the build's
 * configured `VITE_FAIR_DOMAIN` and nothing reads or writes storage, so a
 * stored value cannot silently outlive the flag and pin real visitors to the
 * wrong country.
 *
 * Nothing in the UI shows which fair is overridden. A stored value survives
 * reloads, so a domain that does not resolve leaves the app on an error screen:
 * recover with `/?domain=` (empty), which clears the override, or clear the
 * `vv.fairDomain` key from localStorage.
 */

const STORAGE_KEY = 'vv.fairDomain';

/** The query parameter that selects a fair, e.g. `/?domain=hr.example.com`. */
export const DOMAIN_PARAM = 'domain';

/** Whether the override is compiled into this build at all. */
export const domainOverrideEnabled = import.meta.env.VITE_ALLOW_DOMAIN_OVERRIDE === 'true';

/**
 * The fair this build targets when nothing is stored. Production sets the
 * variable empty — the fair comes from the page's own host there — so blank
 * means unset, not a domain of `''` that callers would have to special-case.
 */
const configuredFairDomain = import.meta.env.VITE_FAIR_DOMAIN?.trim() ?? '';
const CONFIGURED_FAIR_DOMAIN = configuredFairDomain === '' ? undefined : configuredFairDomain;

/**
 * Storage throws rather than returning null in Safari's private mode and
 * wherever site data is blocked. This module is imported at startup, so an
 * uncaught throw here would white-screen the app instead of degrading to the
 * configured fair.
 */
function safeReadStorage(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function safeWriteStorage(value: string | null): void {
  try {
    if (value === null) {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, value);
    }
  } catch {
    // Nothing to do: the override simply does not persist.
  }
}

/** Matches the API's own normalisation, so a stored value resolves server-side. */
export function normaliseDomain(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/:\d+$/, '')
    .replace(/^www\./, '');
}

/**
 * Read on every request rather than cached at module load: the value is written
 * during startup, and a module-level constant would capture whatever was there
 * beforehand.
 */
export function readFairDomain(): string | undefined {
  if (!domainOverrideEnabled) {
    return CONFIGURED_FAIR_DOMAIN;
  }

  const stored = safeReadStorage();

  if (stored !== null && stored !== '') {
    return stored;
  }

  return CONFIGURED_FAIR_DOMAIN;
}

/** Drops the override, returning the app to the configured fair. */
export function clearFairDomain(): void {
  safeWriteStorage(null);
}

export function storeFairDomain(domain: string): void {
  safeWriteStorage(normaliseDomain(domain));
}

/**
 * Applies `?domain=` from the current URL, then strips it.
 *
 * Stripping matters twice over: the parameter would otherwise be re-applied on
 * every reload, and a copied link would pin whoever it was shared with. An
 * empty value (`/?domain=`) is the documented way to reset.
 *
 * Returns true when the stored fair changed and callers must reload — the fair
 * is fetched once at mount, so a value written after that point would not be
 * reflected until the next navigation.
 */
export function applyDomainFromUrl(): boolean {
  if (!domainOverrideEnabled) {
    return false;
  }

  const url = new URL(window.location.href);

  if (!url.searchParams.has(DOMAIN_PARAM)) {
    return false;
  }

  const requested = url.searchParams.get(DOMAIN_PARAM) ?? '';
  const previous = safeReadStorage();

  if (requested.trim() === '') {
    clearFairDomain();
  } else {
    storeFairDomain(requested);
  }

  url.searchParams.delete(DOMAIN_PARAM);
  window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);

  return safeReadStorage() !== previous;
}
